import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import {
  certificationDefinitionsApi,
  ENTITY_TYPES,
  type CertificationDefinitionDto,
  type CreateCertificationDefinitionDto,
  type EntityType,
  type UpdateCertificationDefinitionDto,
} from "@/api/certifications";
import { RolesMultiSelect } from "./RolesMultiSelect";
import { formatGate } from "./gateFormat";

interface Props {
  open: boolean;
  /** When supplied, the dialog opens in edit mode. */
  definition?: CertificationDefinitionDto | null;
  onClose: () => void;
  onSaved: () => void;
}

const LABEL_MAX = 200;
const DESC_MAX = 1000;
const ANY_STAGE_TOKEN = "__any__";

interface FormState {
  entityType: EntityType;
  stageFrom: string | null;
  stageTo: string;
  label: string;
  description: string;
  requiredRoles: string[];
  revokeRoles: string[];
  sortOrder: number;
}

const EMPTY: FormState = {
  entityType: "Broker",
  stageFrom: null,
  stageTo: "",
  label: "",
  description: "",
  requiredRoles: [],
  revokeRoles: [],
  sortOrder: 100,
};

/**
 * Certifications Module v1.0 §4a — create / edit definition dialog. The
 * "tuple" fields (entity type + stage from + stage to) are immutable post-
 * creation per spec §3a, so they're disabled in edit mode.
 *
 * Submission is two-step: the form pane validates locally, then the
 * confirmation pane shows the summary line ("Adding 'MBA Executed' to
 * Broker Onboarding → Certified, certifiable by C0,C1,…") so the admin
 * has one last chance to back out before the POST / PUT lands.
 */
export function CertificationDefinitionEditorDialog({
  open, definition, onClose, onSaved,
}: Props) {
  const isEdit = Boolean(definition);
  const toast = useToast();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [phase, setPhase] = useState<"form" | "confirm">("form");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhase("form");
    setError(null);
    if (definition) {
      setForm({
        entityType: definition.entityType,
        stageFrom: definition.stageFrom,
        stageTo: definition.stageTo,
        label: definition.label,
        description: definition.description ?? "",
        requiredRoles: [...definition.requiredRoles],
        revokeRoles: [...definition.revokeRoles],
        sortOrder: definition.sortOrder,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, definition]);

  const stagesQ = useQuery({
    queryKey: ["certification-stages", form.entityType],
    queryFn: () => certificationDefinitionsApi.stages(form.entityType),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const stages = stagesQ.data ?? [];
  const noStageProvider = !stagesQ.isLoading && !stagesQ.isError && stages.length === 0;

  const stageFromValue =
    form.stageFrom === null ? ANY_STAGE_TOKEN : form.stageFrom;

  const validate = (): string | null => {
    const label = form.label.trim();
    if (!label) return "Label is required.";
    if (label.length > LABEL_MAX) return `Label must be ${LABEL_MAX} characters or fewer.`;
    if (form.description.length > DESC_MAX) {
      return `Description must be ${DESC_MAX} characters or fewer.`;
    }
    if (!form.stageTo.trim()) return "Stage To is required.";
    if (form.requiredRoles.length === 0) {
      return "At least one Required Role must be selected.";
    }
    if (!Number.isFinite(form.sortOrder)) return "Sort Order must be a number.";
    return null;
  };

  const tryAdvance = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setPhase("confirm");
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && definition) {
        const payload: UpdateCertificationDefinitionDto = {
          label: form.label.trim(),
          description: form.description.trim() || null,
          requiredRoles: form.requiredRoles,
          revokeRoles: form.revokeRoles,
          sortOrder: form.sortOrder,
        };
        await certificationDefinitionsApi.update(definition.id, payload);
        toast.success(`Definition '${payload.label}' saved.`);
      } else {
        const payload: CreateCertificationDefinitionDto = {
          entityType: form.entityType,
          stageFrom: form.stageFrom,
          stageTo: form.stageTo.trim(),
          label: form.label.trim(),
          description: form.description.trim() || null,
          requiredRoles: form.requiredRoles,
          revokeRoles: form.revokeRoles,
          sortOrder: form.sortOrder,
        };
        await certificationDefinitionsApi.create(payload);
        toast.success(`Definition '${payload.label}' created.`);
      }
      onSaved();
      onClose();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.errors?.[0] ?? err.message
          : "Could not save definition.";
      setError(msg);
      setPhase("form");
    } finally {
      setSubmitting(false);
    }
  };

  const summary = useMemo(() => formatConfirmSummary(form), [form]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={isEdit ? "Edit certification definition" : "New certification definition"}
      description={
        phase === "confirm"
          ? "Review the change one last time before it lands."
          : isEdit
            ? "Identity (entity type + stage gate) is immutable. Edit the label, description, roles, and sort order below."
            : "Configure a self-attestation item that gates a stage transition."
      }
      size="lg"
      footer={
        phase === "confirm" ? (
          <>
            <Button variant="secondary" onClick={() => setPhase("form")} type="button" disabled={submitting}>
              Back
            </Button>
            <Button onClick={submit} loading={submitting} type="button">
              {isEdit ? "Confirm save" : "Confirm create"}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button onClick={tryAdvance} type="button">
              Continue
            </Button>
          </>
        )
      }
    >
      {phase === "confirm" ? (
        <div className="flex flex-col gap-space-3">
          <p className="text-body text-onyx">{summary}</p>
          {error && <p className="text-small text-status-error" role="alert">{error}</p>}
        </div>
      ) : (
        <div className="flex flex-col gap-space-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
            <FormField label="Entity type" required htmlFor="cd-entity">
              <Select
                id="cd-entity"
                value={form.entityType}
                onChange={(e) => setForm({ ...form, entityType: e.target.value as EntityType, stageFrom: null, stageTo: "" })}
                disabled={isEdit}
              >
                {ENTITY_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Select>
            </FormField>
            <FormField
              label={noStageProvider ? "Stage from (free text)" : "Stage from"}
              htmlFor="cd-stage-from"
            >
              {noStageProvider ? (
                <Input
                  id="cd-stage-from"
                  value={form.stageFrom ?? ""}
                  onChange={(e) => setForm({ ...form, stageFrom: e.target.value || null })}
                  placeholder="(any source stage)"
                  disabled={isEdit}
                />
              ) : (
                <Select
                  id="cd-stage-from"
                  value={stageFromValue}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      stageFrom: e.target.value === ANY_STAGE_TOKEN ? null : e.target.value,
                    })
                  }
                  disabled={isEdit}
                >
                  <option value={ANY_STAGE_TOKEN}>(any source stage)</option>
                  {stages.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.displayName}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField
              label={noStageProvider ? "Stage to (free text)" : "Stage to"}
              required
              htmlFor="cd-stage-to"
            >
              {noStageProvider ? (
                <Input
                  id="cd-stage-to"
                  value={form.stageTo}
                  onChange={(e) => setForm({ ...form, stageTo: e.target.value })}
                  placeholder="Stage code"
                  disabled={isEdit}
                />
              ) : (
                <Select
                  id="cd-stage-to"
                  value={form.stageTo}
                  onChange={(e) => setForm({ ...form, stageTo: e.target.value })}
                  disabled={isEdit}
                >
                  <option value="">— Select stage —</option>
                  {stages.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.displayName}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          </div>

          {noStageProvider && (
            <p className="text-small text-status-warning bg-status-warning/10 border border-status-warning/40 rounded-md px-space-3 py-space-2">
              No stages registered for {form.entityType} yet. The stage dropdowns will populate
              once that entity's module is migrated to the Certifications system. You can save
              definitions using free-text stage codes in the meantime.
            </p>
          )}

          <FormField label="Label" required htmlFor="cd-label">
            <Input
              id="cd-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              maxLength={LABEL_MAX}
              placeholder="MBA Executed"
            />
          </FormField>

          <FormField label="Description" htmlFor="cd-desc">
            <Textarea
              id="cd-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              maxLength={DESC_MAX}
              placeholder="Short admin note explaining when this item should be certified."
            />
            <div className="text-label text-gray-mid mt-space-1">
              {form.description.length}/{DESC_MAX}
            </div>
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
            <FormField
              label="Required roles"
              required
              htmlFor="cd-required"
              helperText="Roles that may certify this item. ADMIN is not a wildcard — list it explicitly if admins should be able to certify."
            >
              <RolesMultiSelect
                id="cd-required"
                value={form.requiredRoles}
                onChange={(next) => setForm({ ...form, requiredRoles: next })}
              />
            </FormField>
            <FormField
              label="Revoke roles"
              htmlFor="cd-revoke"
              helperText="Roles that may revoke an existing certification. Leave empty to inherit from required roles."
            >
              <RolesMultiSelect
                id="cd-revoke"
                value={form.revokeRoles}
                onChange={(next) => setForm({ ...form, revokeRoles: next })}
              />
            </FormField>
          </div>

          <FormField
            label="Sort order"
            htmlFor="cd-sort"
            helperText="Lower numbers render first when multiple items share the same gate. Default 100, step 10."
          >
            <Input
              id="cd-sort"
              type="number"
              value={form.sortOrder}
              step={10}
              onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
            />
          </FormField>

          {error && <p className="text-small text-status-error" role="alert">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

function formatConfirmSummary(form: FormState): string {
  const label = form.label.trim() || "(unnamed)";
  const gate = formatGate(form.stageFrom, form.stageTo || "(unset)");
  const required = form.requiredRoles.length > 0
    ? form.requiredRoles.join(",")
    : "(none)";
  return `Adding '${label}' to ${form.entityType} ${gate}, certifiable by ${required}. Continue?`;
}
